import { useState } from 'react';
import { Search } from 'lucide-react';
import { getOptimizedImageUrl } from '@/lib/image-utils';
import { Button } from '@/components/shared/ui/button';
import { Input } from '@/components/shared/ui/input';
import { useProducts } from '@/features/user/hooks';
import { useNavigate } from 'react-router-dom';
import { Product } from '@/api/customer/products';
import { Sheet, SheetContent, SheetHeader } from '@/components/shared/ui/sheet';
import { useDebounce } from 'use-debounce';
import { useCurrency } from '@/hooks/useCurrency';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchOverlay = ({ isOpen, onClose }: SearchOverlayProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 500); // 500ms delay to avoid overloading
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // Use React Query hook for search with debouncing
  const { data: productsData, isLoading } = useProducts({
    search: debouncedQuery,
    limit: 20
  });

  const products = productsData?.data || [];

  const handleProductClick = (slug: string) => {
    navigate(`/producto/${slug}`);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="w-[95%] sm:max-w-lg font-montserrat h-[93vh] sm:h-[96vh] flex flex-col top-[4vh] sm:top-[2vh] rounded-l-lg border-none shadow-2xl">
        <SheetHeader className="flex-shrink-0 pb-4">
          <div className='flex justify-between items-center border-b border-black pb-2'>
            <div className="relative w-full">
              <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 h-5 w-5 text-black" />
              <Input
                type="text"
                placeholder="BUSCAR..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-4 py-2 font-montserrat text-sm border-none shadow-none focus-visible:ring-0 uppercase tracking-widest placeholder:text-gray-400 h-auto"
                autoFocus
              />
            </div>
            {/* Close button is handled by Sheet primitive, but we can add secondary if needed, though standard is X top right */}
          </div>

          <div className="mt-4 text-xs uppercase tracking-widest text-gray-500 font-montserrat text-left">
            {products.length > 0 ? "Productos" : "Escribe para buscar"}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto pt-2 pr-1" style={{ scrollbarWidth: 'none' }}>
            {isLoading && searchQuery ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-8">
                {products.map((product: Product) => {
                  /* Logic extracted from ProductDetail.tsx for consistency */
                  const getPricingDisplay = () => {
                    // 1. Variable Products
                    if (product.productType === 'variable' && product.variants?.length) {
                      const defaultVariant = product.variants.find((v) => v.isDefault) || product.variants[0];

                      const vSale = defaultVariant.priceSale ?? (defaultVariant as any).price_sale;
                      const vRegular = defaultVariant.priceRegular ?? (defaultVariant as any).price_regular ?? defaultVariant.price;
                      const vCurrent = (vSale && vSale > 0) ? vSale : (vRegular || 0);

                      const hasDiscount = vSale && vRegular && vRegular > vSale;

                      return {
                        price: vCurrent,
                        comparePrice: hasDiscount ? vRegular : undefined,
                        hasDiscount
                      };
                    }

                    // 2. Simple Products
                    const salePrice = product.priceSale ?? (product as any).price_sale;
                    const regularPrice = product.priceRegular ?? (product as any).price_regular;
                    const currentPrice = (salePrice && salePrice > 0) ? salePrice : (regularPrice || 0);
                    const hasDiscount = salePrice && regularPrice && regularPrice > salePrice;

                    return {
                      price: currentPrice,
                      comparePrice: hasDiscount ? regularPrice : undefined,
                      hasDiscount: !!hasDiscount // Ensure boolean
                    };
                  };

                  const { price, comparePrice, hasDiscount } = getPricingDisplay();

                  return (
                    <button
                      key={product.id}
                      onClick={() => handleProductClick(product.slug)}
                      className="text-center group cursor-pointer flex flex-col items-center"
                    >
                      <div className="w-full aspect-[4/5] bg-gray-100 mb-3 overflow-hidden">
                        {product.images?.[0] ? (
                          <img
                            src={getOptimizedImageUrl(product.images[0].imageUrl, { width: 400, quality: 80 })}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300 font-montserrat text-xs bg-gray-50">
                            SIN IMAGEN
                          </div>
                        )}
                      </div>
                      <div className="space-y-1 w-full px-1">
                        <p className="font-medium text-[11px] leading-tight font-montserrat text-black uppercase tracking-wide line-clamp-2">
                          {product.name}
                        </p>
                        <div className="flex items-center justify-center gap-2">
                          {hasDiscount && comparePrice && (
                            <span className="text-[12px] text-gray-400 line-through font-montserrat">
                              {formatPrice(comparePrice)}
                            </span>
                          )}
                          <p className={`text-[14px] font-bold font-montserrat tracking-wide ${hasDiscount ? 'text-red-600' : 'text-black'}`}>
                            {formatPrice(price)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : debouncedQuery ? (
              <div className="text-center py-12">
                <p className="text-sm font-medium mb-2 font-montserrat uppercase tracking-wider">
                  No hubo resultadossss
                </p>
                <p className="text-xs text-muted-foreground font-montserrat">
                  Intenta con otra palabra
                </p>
              </div>
            ) : null}
          </div>

          {/* Footer Actions */}
          <div className="pt-4 mt-auto pb-2">
            <Button
              className="w-full bg-[#344c3d] hover:bg-[#d8959b] text-white font-montserrat uppercase tracking-[0.2em] text-[13px] h-12 rounded-none "
              onClick={() => {
                navigate('/tienda');
                onClose();
              }}
            >
              Ver Todo
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
